import { createEnrichmentIsland } from '@/utils/islands';

type AnchorLink = { link: HTMLAnchorElement; target: HTMLElement };

function findActive(links: AnchorLink[], bar: Element) {
  const scrolledToBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 1;
  if (scrolledToBottom) {
    return links[links.length - 1];
  }

  // The section counts as current once its top passes the upper third of the area below the sticky bar
  const offset = bar.getBoundingClientRect().bottom;
  const threshold = offset + (window.innerHeight - offset) / 3;
  return links.filter(x => x.target.getBoundingClientRect().top <= threshold).pop() ?? links[0];
}

// The bar sticks below the header, and jumps to a section must clear both
function trackStickyHeights(bar: HTMLElement) {
  const header = document.querySelector<HTMLElement>('body > header');
  const root = document.documentElement;
  const apply = () => {
    const headerHeight = header?.offsetHeight ?? 0;
    root.style.setProperty('--header-height', `${headerHeight}px`);
    // the bar's stuck top can sit partly under the header (mobile), so read it rather than assume headerHeight
    root.style.scrollPaddingTop = `${parseFloat(getComputedStyle(bar).top) + bar.offsetHeight}px`;
  };

  const observer = new ResizeObserver(apply);
  observer.observe(bar);
  if (header) {
    observer.observe(header);
  }
}

function initAnchorLinks() {
  createEnrichmentIsland('.anchor-links', nav => {
    const links = Array.from(nav.querySelectorAll<HTMLAnchorElement>('a[href^="#"]'))
      .map(link => ({ link, target: document.getElementById(decodeURIComponent(link.hash.slice(1))) }))
      .filter((x): x is AnchorLink => x.target !== null)
      // findActive walks the sections top to bottom, and editors may order the links differently
      .sort((a, b) => a.target.compareDocumentPosition(b.target) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1);

    // Items render hidden, so links to sections that aren't on this page stay out of sight and out of the accessibility tree
    links.forEach(x => x.link.closest('li')?.removeAttribute('hidden'));

    const bar = nav.closest<HTMLElement>('.anchor-links-container') ?? nav as HTMLElement;
    if (links.length === 0) {
      bar.hidden = true;
      return;
    }

    nav.classList.toggle('dark', !!document.querySelector('.breadcrumb-container.dark'));
    trackStickyHeights(bar);

    let current: AnchorLink | undefined;
    let frame = 0;
    const update = () => {
      frame = 0;
      const active = findActive(links, bar);
      if (active === current) {
        return;
      }

      current?.link.removeAttribute('aria-current');
      active.link.setAttribute('aria-current', 'location');
      current = active;
    };

    window.addEventListener('scroll', () => {
      frame ||= requestAnimationFrame(update);
    }, { passive: true });
    update();
  });
}

export default initAnchorLinks;
